/**
 * Moon Basin - warm the NEXT batch's pictures on the Worlds collection pages. 2026-10-02 (Simon).
 * Jay, phone on a weak signal: scrolling the Worlds wall, whole rows showed a title and price with an empty picture
 * ("Worlds really slowed down at these points"). The theme already fetches the next batch's HTML ahead of time, but
 * a batch's pictures only start downloading when its cards are added to the page (1000 px before the end of the
 * current batch), so a fast scroll outruns them.
 * This script does not change when batches are added (the 16-cards-first rule of 2026-10-01 stays). It listens to the
 * same section fetches assets/paginated-list.js makes and, once the page itself has finished loading, asks the
 * browser for the first picture of each card in the fetched batch at LOW priority, with the card's own srcset and
 * sizes, so the browser picks the same file and the card paints from cache when it is added. Cards whose picture is
 * marked lazy (colourway cards that will be folded away) are skipped.
 * Loaded from sections/main-collection.liquid. Revert = delete that script line and this file.
 *
 * 2026-10-02 PM (Simon, Jay driving: "Bonevine Oxblood Hoodie is the last thing to load on the Worlds page. It
 * doesn't even go beyond that"). assets/section-renderer.js fetches a batch with no timeout, no retry and no status
 * check. Measured in WebKit: ONE batch request that hangs (no answer, no error - what a moving phone does) stops the
 * live page at batch 2 for good, because every later request for that batch waits on the same stuck promise.
 * Now each batch request gets its own abort signal (so it is never shared with a stuck one) and a time limit
 * (12 s, then 15 s), and is retried up to 3 more times (after 1.5 s, 3 s, 6 s, bypassing the cache) when it hangs,
 * throws, or comes back without product cards. A request the caller aborted is not retried.
 * Revert this step = replace the getSectionHTML wrapper below with the 2026-10-02 morning version (no retry loop).
 */
import { sectionRenderer } from '@theme/section-renderer';

const kept = [];
const seen = new Set();

/** @param {string} html */
function warm(html) {
  if (!html || typeof html !== 'string') return;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const cards = doc.querySelectorAll('[ref="grid"] > [ref="cards[]"]');
  for (const card of cards) {
    const img = card.querySelector('img.product-media__image');
    if (!img || img.getAttribute('loading') === 'lazy') continue;
    const srcset = img.getAttribute('srcset');
    if (!srcset || seen.has(srcset)) continue;
    seen.add(srcset);
    const pre = new Image();
    pre.decoding = 'async';
    pre.setAttribute('fetchpriority', 'low');
    pre.sizes = img.getAttribute('sizes') || '50vw';
    pre.srcset = srcset;
    kept.push(pre);
  }
}

/** @param {() => void} fn */
function whenLoaded(fn) {
  if (document.readyState === 'complete') fn();
  else window.addEventListener('load', fn, { once: true });
}

/** @param {unknown} url */
function isBatchUrl(url) {
  try {
    return !!url && new URL(String(url), location.href).searchParams.has('page');
  } catch (error) {
    return false;
  }
}

/** @param {unknown} html */
function looksLikeBatch(html) {
  return typeof html === 'string' && html.includes('cards[]');
}

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const RETRY_DELAYS = [1500, 3000, 6000];
const TIME_LIMITS = [12000, 15000, 15000, 15000];

const original = sectionRenderer.getSectionHTML.bind(sectionRenderer);
sectionRenderer.getSectionHTML = async (...args) => {
  const url = args[2];
  /** @type {AbortSignal | undefined} */
  const callerSignal = args[3];
  if (!isBatchUrl(url)) return original(...args);

  let html;
  let lastError;
  for (let attempt = 0; attempt <= RETRY_DELAYS.length; attempt++) {
    if (attempt > 0) {
      if (callerSignal && callerSignal.aborted) break;
      await wait(RETRY_DELAYS[attempt - 1]);
      if (callerSignal && callerSignal.aborted) break;
    }
    const controller = new AbortController();
    const onCallerAbort = () => controller.abort();
    if (callerSignal) callerSignal.addEventListener('abort', onCallerAbort, { once: true });
    const timer = setTimeout(() => controller.abort(), TIME_LIMITS[attempt]);
    try {
      const callArgs = args.slice();
      callArgs[1] = attempt === 0 ? args[1] : false; // retries skip the cache: it may hold a failed answer
      callArgs[2] = url;
      callArgs[3] = controller.signal; // own signal: never share a stuck request
      html = await original(...callArgs);
      if (looksLikeBatch(html)) {
        lastError = undefined;
        break;
      }
      lastError = new Error('batch came back without product cards');
    } catch (error) {
      lastError = error;
      if (callerSignal && callerSignal.aborted) throw error;
    } finally {
      clearTimeout(timer);
      if (callerSignal) callerSignal.removeEventListener('abort', onCallerAbort);
    }
  }
  if (lastError && !looksLikeBatch(html)) throw lastError;

  try {
    whenLoaded(() => warm(html));
  } catch (error) {
    /* warming is optional; never break the batch itself */
  }
  return html;
};

// The first look-ahead fetch can run before this module does; ask for it again (the renderer answers from its cache).
whenLoaded(() => {
  const list = document.querySelector('results-list[section-id]');
  const cards = list ? list.querySelectorAll('[ref="cards[]"][data-page]') : [];
  if (!list || !cards.length) return;
  const last = Number(cards[cards.length - 1].getAttribute('data-page')) || 1;
  const lastPage = Number(list.querySelector('[ref="grid"]')?.getAttribute('data-last-page')) || 0;
  if (!lastPage || last + 1 > lastPage) return;
  const url = new URL(location.href);
  url.searchParams.set('page', String(last + 1));
  url.hash = '';
  sectionRenderer.getSectionHTML(list.getAttribute('section-id'), true, url).catch(() => {});
});
